import { Injectable } from '@angular/core';
import { GridData, GridHeader, GridDetails } from '../interfaces/grid';
import { cloneDeep } from 'lodash';
import * as XLSX from 'xlsx';
import { VisitData, VisitDetails, VisitHeader } from '../interfaces/visit-data';
import { SampleData, SampleDetails, SampleHeader, SampleStatus } from '../interfaces/sample-details';
import * as moment from 'moment';

@Injectable()
export class TrackingOverviewService {

    public sampleAliquotsHeaderData: GridHeader[] = [
        {
            displayText: 'Spec. Mgmt. Hold',
            key: 'isOnSMHold',
            isHoldIndicator: true,
            sort: 'asc',
            skipForDownload: true
        },
        {
            displayText: 'Accession',
            key: 'accessionNumber',
            sort: 'asc'
        },
        {
            displayText: 'Container №',
            key: 'containerNumber',
            sort: null
        },
        {
            displayText: 'Container Barcode',
            key: 'barcode',
            sort: null,
            isLink: true
        },
        {
            displayText: 'Container Receipt Date',
            key: 'containerReceiptDate',
            sort: null,
            isDateTime: true
        },
        {
            displayText: 'Expected Receipt Condition',
            key: 'expectedReceiptCondition',
            sort: null
        },
        {
            displayText: 'Aliquoted/Aliquot Created Date',
            key: 'aliquotCreatedDate',
            sort: null,
            isDate: true
        },
        {
            displayText: 'Container Label Line 1',
            key: 'containerLabelLine1',
            sort: null
        },
        {
            displayText: 'Container Label Line 2',
            key: 'containerLabelLine2',
            sort: null
        },
        {
            displayText: 'Current Sample Status',
            key: 'currentSampleStatus',
            sort: null
        },
        {
            displayText: 'Cancellation Description',
            key: 'cancellationDescription',
            sort: null
        },
        {
            displayText: 'Specimen Type',
            key: 'specimenType',
            sort: null
        }
    ];

    public visitDetailsHeading: VisitHeader[] = [
        {
            displayText: 'Accession',
            key: 'accessionNumber',
        },
        {
            displayText: 'Protocol Code',
            key: 'protocolCode'
        },
        {
            displayText: 'Project №',
            key: 'projectNumber'
        },
        {
            displayText: 'Investigator Name/№',
            key: 'investigatorNameAndNo'
        },
        {
            displayText: 'Country',
            key: 'country'
        },
        {
            displayText: 'Screening №',
            key: 'screeningNumber'
        },
        {
            displayText: 'Patient №',
            key: 'patientNumber'
        },
        {
            displayText: 'Randomization №',
            key: 'randomizationNumber'
        },
        {
            displayText: 'Birthdate/Age',
            key: 'displayAge'
        },
        {
            displayText: 'Visit Name/PVC',
            key: 'visitNameAndPvc'
        },
        {
            displayText: 'Kit Type',
            key: 'kitType'
        },
        {
            displayText: 'Collection Date',
            key: 'specimenCollectedDate',
            isDateTime: true
        },
        {
            displayText: 'Kit Receipt Date',
            key: 'kitReceivedDate',
            isDateTime: true
        }
    ];

    public sampleDetailsHeading: SampleHeader[] = [
        {
            displayText: 'Container Barcode',
            key: 'barcode',
        },
        {
            displayText: 'Container Receipt Date',
            key: 'containerReceiptDate',
            isDateTime: true
        },
        {
            displayText: 'Expected Receipt Condition',
            key: 'expectedReceiptCondition'
        },
        {
            displayText: 'Actual Receipt Condition',
            key: 'actualReceiptCondition'
        },
        {
            displayText: 'Specimen Type',
            key: 'specimenType'
        },
        {
            displayText: 'Container Label Line 1',
            key: 'containerLabelLine1'
        },
        {
            displayText: 'Container Label Line 2',
            key: 'containerLabelLine2'
        },
        {
            displayText: 'Aliquoted/Aliquot Created Date',
            key: 'aliquotedDate',
            isDate: true
        },
    ];

    public sampleDetailsHeadingForDiscarded: SampleHeader[] = [
        ...this.sampleDetailsHeading,
        {
            displayText: 'Sample Registered Date',
            key: 'sampleRegisteredDate',
            isDateTime: true
        },
        {
            displayText: 'Cancellation Description',
            key: 'cancellationReason'
        }
    ];

    public sampleDetailsHeadingForCancelled: SampleHeader[] = [
        ...this.sampleDetailsHeading.filter(t => t.key !== 'aliquotedDate'),
        {
            displayText: 'Current Sample Status',
            key: 'currentSampleStatus'
        },
        {
            displayText: 'Cancellation Reason',
            key: 'cancellationReason'
        }
    ];

    public sampleDetailsHeadingForInInventoryOrShipped: SampleHeader[] = [
        ...this.sampleDetailsHeading,
        {
            displayText: 'Sample Registered Date',
            key: 'sampleRegisteredDate',
            isDateTime: true
        },
        {
            displayText: 'SMART Specimen Class',
            key: 'smartSpecimenClass'
        },
        {
            displayText: 'SMART Specimen Class Description',
            key: 'smartSpecimenClassDescription'
        }
    ];

    public sampleDetailsHeadingForNotReceived: SampleHeader[] = [
        ...this.sampleDetailsHeading,
        {
            displayText: 'Number of Days Pending Receipt',
            key: 'pendingDaysReceipt'
        },
        {
            displayText: 'Notification 1 Sent Date',
            key: 'notification1SentDate',
            isDateTime: true
        },
        {
            displayText: 'Notification 1 Sent To',
            key: 'notification1SentTo'
        },
        {
            displayText: 'Notification 2 Sent Date',
            key: 'notification2SentDate',
            isDateTime: true
        },
        {
            displayText: 'Notification 2 Sent To',
            key: 'notification2SentTo'
        },
        {
            displayText: 'Notification 3 Sent Date',
            key: 'notification3SentDate',
            isDateTime: true
        },
        {
            displayText: 'Notification 3 Sent To',
            key: 'notification3SentTo'
        }
    ];

    public aliquotsGridHeader: GridHeader[] = [
        {
            displayText: 'Container Barcode',
            key: 'barcode',
            sort: null,
            isLink: true
        },
        {
            displayText: 'Aliquot Created Date',
            key: 'aliquotCreatedDate',
            sort: null,
            isDate: true
        },
        {
            displayText: 'Container Label Line 1',
            key: 'containerLabelLine1',
            sort: null
        },
        {
            displayText: 'Container Label Line 2',
            key: 'containerLabelLine2',
            sort: null
        },
        {
            displayText: 'Current Sample Status',
            key: 'currentSampleStatus',
            sort: null
        },
        {
            displayText: 'Specimen Type',
            key: 'specimenType',
            sort: null
        }
    ];

    public createReportForSample(gridDetails: GridDetails, visitDetails: VisitDetails, sampleDetails: SampleDetails, status: SampleStatus, fileNameToDownload: string) {
        const clonedGridData = cloneDeep(gridDetails);
        const clonedVisitData = cloneDeep(visitDetails);
        const clonedSampleData = cloneDeep(sampleDetails);

        let reportData: GridData[] = this.flatternGridData(clonedGridData.data);

        const header = this.constructHeaderForReportDownload(gridDetails, visitDetails, sampleDetails);
        const downloadData = this.constructDownloadModelForSample(reportData, clonedVisitData.data, clonedSampleData.data, status);
        this.exportAsExcel(header, downloadData, fileNameToDownload);
    }

    public createReportForVisit(gridDetails: GridDetails, visitDetails: VisitDetails, fileNameToDownload: string) {
        const clonedGridData = cloneDeep(gridDetails);
        const clonedVisitData = cloneDeep(visitDetails) || { data: null, header: null };

        let reportData: GridData[] = this.flatternGridData(clonedGridData.data);

        clonedGridData.header.splice(10, 0, { displayText: 'Parent Barcode', key: 'parentDataForDownload' }, { displayText: 'Children Barcode', key: 'childDataForDownload' });
        const header = this.constructHeaderForReportDownload(clonedGridData, clonedVisitData);
        const downloadData = this.constructDownloadModelForVisit(reportData, clonedVisitData.data);
        this.exportAsExcel(header, downloadData, fileNameToDownload);
    }

    private flatternGridData(clonedGridData: GridData[]) {
        let reportData: GridData[] = [];

        clonedGridData.forEach((gd: GridData) => {
            reportData.push(gd);

            if (gd.expandData) {
                if (gd.expandData.child && gd.expandData.child.length) {
                    gd.childDataForDownload = [];
                    gd.expandData.child.forEach((children: GridData) => {
                        gd.childDataForDownload.push(children.barcode);
                    });
                }

                if (gd.expandData.parent && gd.expandData.parent.length) {
                    gd.parentDataForDownload = [];
                    gd.expandData.parent.forEach((parent: GridData) => {
                        gd.parentDataForDownload.push(parent.barcode);
                    });
                }

                delete gd.expandData;
            }
        });
        return reportData;
    }

    private constructDownloadModelForVisit(cgv: GridData[], cvd: VisitData) {
        return {
            vd: this.visitModelForDownload(cvd),
            gd: this.sampleAliquotsModelForDownload(cgv)
        };
    }

    private constructDownloadModelForSample(cgv: GridData[], cvd: VisitData, csd: SampleData, status: SampleStatus) {
        return {
            vd: this.visitModelForDownload(cvd),
            gd: this.aliquotsModelForDownload(cgv),
            sd: this.sampleModelForDownload(csd, status)
        };
    }

    private exportAsExcel(headers: { title: string, key: string, header: string[] }[], data: any, fileNameToDownload: string) {
        const wb = XLSX.utils.book_new();
        headers.forEach(h => {
            const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
            XLSX.utils.sheet_add_aoa(ws, [h.header]);
            XLSX.utils.sheet_add_json(ws, data[h.key], { origin: 'A2', skipHeader: true });
            XLSX.utils.book_append_sheet(wb, ws, h.title);
        });
        XLSX.writeFile(wb, fileNameToDownload + '.xlsx');
    }

    private visitModelForDownload(cvd: VisitData) {
        if (!cvd) { return null; }
        return [{
            accessionNumber: cvd.accessionNumber,
            protocolCode: cvd.protocolCode,
            projectNumber: cvd.projectNumber,
            investigatorNameAndNo: cvd.investigatorNameAndNo,
            country: cvd.country,
            screeningNumber: cvd.screeningNumber,
            patientNumber: cvd.patientNumber,
            randomizationNumber: cvd.randomizationNumber,
            displayAge: cvd.displayAge,
            visitNameAndPvc: cvd.visitNameAndPvc,
            kitType: cvd.kitType,
            specimenCollectedDate: cvd.specimenCollectedDate ? moment(cvd.specimenCollectedDate).format('DD-MMM-YYYY HH:mm') : '',
            kitReceivedDate: cvd.kitReceivedDate ? moment(cvd.kitReceivedDate).format('DD-MMM-YYYY HH:mm') : '',
        }];
    }

    private sampleModelForDownload(csd: SampleData, status: SampleStatus) {
        if (!csd) { return null; }
        let sampleData = {
            containerBarcode: csd.barcode,
            containerReceiptDate: csd.containerReceiptDate ? moment(csd.containerReceiptDate).format('DD-MMM-YYYY HH:mm') : '',
            expectedReceiptCondition: csd.expectedReceiptCondition,
            actualReceiptCondition: csd.actualReceiptCondition,
            specimenType: csd.specimenType,
            containerLabelLine1: csd.containerLabelLine1,
            containerLabelLine2: csd.containerLabelLine2,
            aliquotedDate: csd.aliquotedDate ? moment(csd.aliquotedDate).format('DD-MMM-YYYY') : '',
        } as SampleData;

        if (status === SampleStatus.InInventory || status === SampleStatus.Shipped
            || status === SampleStatus.Discarded) {
            sampleData = {
                ...sampleData,
                sampleRegisteredDate: csd.sampleRegisteredDate ? moment(csd.sampleRegisteredDate).format('DD-MMM-YYYY HH:mm') : '',
            };
        }

        if (status === SampleStatus.InInventory || status === SampleStatus.Shipped) {
            sampleData = {
                ...sampleData,
                smartSpecimenClass: csd.smartSpecimenClass,
                smartSpecimenClassDescription: csd.smartSpecimenClassDescription,
            };
        }

        if (status === SampleStatus.Cancelled) {
            const data = cloneDeep(sampleData); delete data.aliquotedDate;
            sampleData = {
                ...data,
                currentSampleStatus: csd.currentSampleStatus,
                cancellationReason: csd.cancellationReason,
            };
        }

        if (status === SampleStatus.NotReceived) {
            sampleData = {
                ...sampleData,
                cancellationReason: csd.cancellationReason,
                pendingDaysReceipt: csd.pendingDaysReceipt,
                notification1SentDate: csd.notification1SentDate,
                notification1SentTo: csd.notification1SentTo,
                notification2SentDate: csd.notification2SentDate,
                notification2SentTo: csd.notification2SentTo,
                notification3SentDate: csd.notification3SentDate,
                notification3SentTo: csd.notification3SentTo,
            };
        }
        return [sampleData];
    }

    private sampleAliquotsModelForDownload(gridData: GridData[]) {
        return gridData.map(gd => {
            return {
                accessionNumber: gd.accessionNumber,
                containerNumber: gd.containerNumber,
                barcodeNumber: gd.barcode,
                containerReceiptDate: gd.containerReceiptDate ? moment(gd.containerReceiptDate).format('DD-MMM-YYYY HH:mm') : '',
                expectedReceiptCondition: gd.expectedReceiptCondition,
                aliquotCreatedDate: gd.aliquotCreatedDate ? moment(gd.aliquotCreatedDate).format('DD-MMM-YYYY') : '',
                containerLabelLine1: gd.containerLabelLine1,
                containerLabelLine2: gd.containerLabelLine2,
                currentSampleStatus: gd.currentSampleStatus,
                parentDataForDownload: gd.parentDataForDownload && gd.parentDataForDownload.join(),
                childDataForDownload: gd.childDataForDownload && gd.childDataForDownload.join(),
                cancellationDescription: gd.cancellationDescription,
                specimenType: gd.specimenType
            };
        });
    }

    private aliquotsModelForDownload(gridData: GridData[]) {
        return gridData.map(gd => {
            return {
                barcodeNumber: gd.barcode,
                aliquotCreatedDate: gd.aliquotCreatedDate ? moment(gd.aliquotCreatedDate).format('DD-MMM-YYYY') : '',
                containerLabelLine1: gd.containerLabelLine1,
                containerLabelLine2: gd.containerLabelLine2,
                currentSampleStatus: gd.currentSampleStatus,
                specimenType: gd.specimenType,
                parentDataForDownload: gd.parentDataForDownload && gd.parentDataForDownload.join(),
                childDataForDownload: gd.childDataForDownload && gd.childDataForDownload.join()
            };
        });
    }

    private constructHeaderForReportDownload(gridDetails: GridDetails, visitDetails: VisitDetails, sampleDetails?: SampleDetails) {
        const header = [];
        const gridTitle = (sampleDetails && sampleDetails.data) ? 'Aliquot Details' : 'Sample & Aliquot Details';

        if (visitDetails.data) {
            header.push({
                title: 'Visit Details',
                key: 'vd',
                header: visitDetails.header.map(gh => gh.displayText)
            });
        }

        if (sampleDetails && sampleDetails.data) {
            header.push({
                title: 'Sample Details',
                key: 'sd',
                header: sampleDetails.header.map(sdh => sdh.displayText)
            });
        }

        if (gridDetails && gridDetails.data && gridDetails.data.length) {
            header.push({
                title: gridTitle,
                key: 'gd',
                header: gridDetails.header.filter(vdh => !vdh.skipForDownload).map(vdh => vdh.displayText)
            });
        }
        return header;
    }
}